import { FC, useCallback, useContext, useState } from 'react';
import { Box, Stack, Step, StepLabel, Stepper, useTheme } from '@mui/material';

import { TributeCardWizardContext } from '@modules/Tributes/contexts/TributeCardWizardContext/TributeCardWizardContext';
import { SelectImageStep } from './SelectImageStep';
import { EditImageStep } from './EditImageStep';
import { DeliveryDetailsStep } from './DeliveryDetailsStep';
import { ConfirmationStep } from './ConfirmationStep';


type StepIconProps = {
    active: boolean;
    completed: boolean;
    icon: React.ReactNode;
};

const StepIcon: FC<StepIconProps> = (props) => {
  const { active, completed, icon } = props;
  const theme = useTheme();

  const getColor = () => {
    if (active) return {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    };
    if (completed) return {
      background: '#fcdcda',
      color: theme.palette.secondary3.main,
    };
    return {
      background: '#e6e6e8',
      color: theme.palette.primary2.main,
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: getColor().background,
        color: getColor().color,
        fontWeight: 'bold',
        fontSize: 14,
      }}
    >
      {icon}
    </Box>
  );
};

type Props = {
    onComplete?: () => void;
}

export const CreateTributeCardWizard: FC<Props> = (props) => {
  const { onComplete } = props;
  const { currentStep, onCreateCard, currentStepIndex, steps } = useContext(TributeCardWizardContext);
  const [isSavingTribute, setIsSavingTribute] = useState(false);

  const onSave = useCallback(async () => {
    setIsSavingTribute(true);
    try {
      await onCreateCard();
      onComplete?.();
    } catch (error) {
      console.error('Error saving tribute card:', error);
    } finally {
      setIsSavingTribute(false);
    }

  }, [onComplete, onCreateCard]);


  return (

    <Box>
      <Stack direction="row"  justifyContent="center" alignItems="center" mb={2}>
        <Stepper activeStep={currentStepIndex} connector={null}>
          {steps.map((step, index) => {
            return (
              <Step
                key={index}
                completed={currentStepIndex > index}
                sx={{
                  '&.MuiStep-root': {
                    padding: 0,
                  },
                  'svg': {
                    width: '32px',
                    height: '32px',
                  },
                }}
              >
                <StepLabel
                  slots={{
                    stepIcon: StepIcon,
                  }}
                />
              </Step>
            );
          })}
        </Stepper>
      </Stack>
      {currentStep === 'selectImage' && (
        <SelectImageStep />
      )}
      {currentStep === 'positionImage' && (
        <EditImageStep />
      )}
      {currentStep === 'deliveryDetails' && (
        <DeliveryDetailsStep />
      )}
      {currentStep === 'review' && (
        <ConfirmationStep
          onSubmit={onSave}
          slots={{
            submitButton: {
              loading: isSavingTribute,
            },
          }}
        />
      )}
    </Box>
  );
};
